import { sha256Prefix16 } from './sha256.js'

export const AUTH_TRACE_PATH = '/front/diagnostics/auth_trace'
export const AUTH_TRACE_SOURCE = 'dyxh_mp'

const MAX_BODY_BYTES = 4096
const ALLOWED_KEYS = [
  'source',
  'event',
  'account_id',
  'page_route',
  'route',
  'api_path',
  'error_code',
  'reason_code',
  'token_exists',
  'refresh_exists',
  'token_hash',
  'refresh_hash',
  'device_hash',
  'token_source',
  'clear_caller',
  'clear_reason',
  'request_id',
  'client_ts',
  'client_seq',
  'app_version',
  'platform',
]

const SEQ_KEY = 'AUTH_TRACE_SEQ'

const clip = (value, max) => {
  if (value == null || value === '') return ''
  return String(value).slice(0, max)
}

const isHexHash = (value) => /^[0-9a-f]{16,64}$/i.test(String(value || ''))

const toClientTs = () => {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  const offset = -d.getTimezoneOffset()
  const sign = offset >= 0 ? '+' : '-'
  const abs = Math.abs(offset)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}:${pad(d.getSeconds())}${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
}

const nextSeq = () => {
  let seq = 0
  try {
    seq = Number(localStorage.getItem(SEQ_KEY) || 0) || 0
  } catch (e) {
    seq = 0
  }
  seq += 1
  try {
    localStorage.setItem(SEQ_KEY, String(seq))
  } catch (e) {
    // ignore
  }
  return String(seq)
}

const nextRequestId = (seq) => `auth-${Date.now()}-${String(seq).padStart(6, '0')}`.slice(0, 64)

export const hashAuthValue = sha256Prefix16

export const normalizeApiPath = (url = '') => {
  const text = String(url || '')
  if (!text) return ''
  try {
    if (/^https?:\/\//i.test(text)) {
      return new URL(text).pathname.slice(0, 256)
    }
  } catch (e) {
    // ignore
  }
  return text.split('?')[0].slice(0, 256)
}

export const getPageRoute = () => {
  try {
    return clip(window.location.pathname || '', 256)
  } catch (e) {
    return ''
  }
}

export const getH5AuthSnapshot = () => {
  let token = ''
  let refreshToken = ''
  let deviceId = ''
  let accountId = ''
  try {
    token = localStorage.getItem('tokenLogin') || localStorage.getItem('token') || ''
    refreshToken = localStorage.getItem('refresh_token') || ''
    deviceId = localStorage.getItem('device_id') || ''
    accountId = localStorage.getItem('uid') || ''
  } catch (e) {
    // ignore
  }
  return { token, refreshToken, deviceId, accountId }
}

const joinUrl = (base, path) => {
  const prefix = String(base || '').replace(/\/+$/, '')
  const suffix = String(path || '').replace(/^\/+/, '')
  return `${prefix}/${suffix}`
}

const sanitize = (payload) => {
  const next = { source: AUTH_TRACE_SOURCE }
  ALLOWED_KEYS.forEach((key) => {
    if (payload[key] == null || payload[key] === '') return
    next[key] = payload[key]
  })
  if (next.account_id) next.account_id = clip(next.account_id, 32)
  if (next.page_route) next.page_route = clip(next.page_route, 256)
  if (next.route) next.route = clip(next.route, 256)
  if (next.api_path) next.api_path = clip(next.api_path, 256)
  if (next.error_code) next.error_code = clip(next.error_code, 32)
  if (next.reason_code) next.reason_code = clip(next.reason_code, 128)
  if (next.token_source) next.token_source = clip(next.token_source, 32)
  if (next.clear_caller) next.clear_caller = clip(next.clear_caller, 128)
  if (next.clear_reason) next.clear_reason = clip(next.clear_reason, 128)
  if (next.request_id) next.request_id = clip(next.request_id, 64)
  if (next.client_ts) next.client_ts = clip(next.client_ts, 64)
  if (next.client_seq) next.client_seq = clip(next.client_seq, 32)
  if (next.app_version) next.app_version = clip(next.app_version, 32)
  if (next.platform) next.platform = clip(next.platform, 32)
  ;['token_hash', 'refresh_hash', 'device_hash'].forEach((key) => {
    if (next[key] && !isHexHash(next[key])) delete next[key]
  })
  if (JSON.stringify(next).length <= MAX_BODY_BYTES) return next
  ;['route', 'page_route', 'app_version', 'platform'].forEach((key) => {
    delete next[key]
  })
  if (JSON.stringify(next).length <= MAX_BODY_BYTES) return next
  return {
    source: AUTH_TRACE_SOURCE,
    event: next.event,
    request_id: next.request_id,
    client_ts: next.client_ts,
  }
}

export const isAuthTraceRequest = (url = '') => String(url || '').indexOf('diagnostics/auth_trace') !== -1

const isProductionDeploy = () => String(process.env.VUE_APP_DEPLOY_TYPE || '').trim().toLowerCase() === 'production'

export const reportAuthTrace = (input = {}) => {
  try {
    const event = String(input.event || '')
    if (!event) return
    // 生产环境请求级诊断量过大，停止 TOKEN_READ_FOR_REQUEST；其它认证事件仍全量上报
    if (event === 'TOKEN_READ_FOR_REQUEST' && isProductionDeploy()) return

    const snapshot = getH5AuthSnapshot()
    const token = input.token != null ? input.token : snapshot.token
    const refreshToken = input.refresh_token != null ? input.refresh_token : snapshot.refreshToken
    const deviceId = input.device_id != null ? input.device_id : snapshot.deviceId
    const accountId = input.account_id != null ? input.account_id : snapshot.accountId
    const seq = nextSeq()
    const payload = sanitize({
      source: AUTH_TRACE_SOURCE,
      event,
      account_id: accountId ? String(accountId) : '',
      page_route: input.page_route || getPageRoute(),
      route: input.route || '',
      api_path: input.api_path ? normalizeApiPath(input.api_path) : '',
      error_code: input.error_code == null || input.error_code === '' ? '' : String(input.error_code),
      reason_code: input.reason_code || '',
      token_exists: typeof input.token_exists === 'boolean' ? input.token_exists : !!token,
      refresh_exists: typeof input.refresh_exists === 'boolean' ? input.refresh_exists : !!refreshToken,
      token_hash: token ? sha256Prefix16(token) : '',
      refresh_hash: refreshToken ? sha256Prefix16(refreshToken) : '',
      device_hash: deviceId ? sha256Prefix16(deviceId) : '',
      token_source: input.token_source || '',
      clear_caller: input.clear_caller || '',
      clear_reason: input.clear_reason || '',
      request_id: input.request_id || nextRequestId(seq),
      client_ts: input.client_ts || toClientTs(),
      client_seq: seq,
      app_version: input.app_version || clip(process.env.VUE_APP_MINIAPP_VERSION || '0.1.0', 32),
      platform: input.platform || 'h5',
    })

    const baseURL = process.env.VUE_APP_API_BASE_URL || ''
    if (!baseURL || typeof fetch !== 'function') return

    fetch(joinUrl(baseURL, AUTH_TRACE_PATH), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'omit',
      keepalive: true,
    }).catch(() => {})
  } catch (e) {
    // 上报失败不影响登录主链路
  }
}
